/**
 * StableFetchProgressBanner — global banner showing stable-fetch backfill progress.
 *
 * The backend (/api/accumulated-data?source=stable) may return `partial: true`
 * for the first response of a new multi-expiration window (weekly, monthly,
 * 6-month, annual — whatever range the user picked in StableDataFetchBar):
 * nearest expirations are returned immediately while the remaining expirations
 * are backfilled in the background (~30–60s for 6-month, up to ~120s for 1-year).
 * `stableAccumulatedRequest` transparently polls until the dataset completes;
 * this component subscribes to that status feed and surfaces:
 *
 *   • "Loading remaining expirations…"  while any URL is still partial
 *   • "Data may be incomplete"          when the attempt budget is exhausted
 *
 * Note: This is a status indicator only. The existing `StableDataFetchBar`
 * (the typeahead + Fetch button) is unrelated and still controls what gets
 * fetched; do not confuse the two.
 *
 * Render this banner once at the app root (mounted in App.tsx in both simple
 * + advanced modes). It self-hides when there is nothing to report.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    subscribeStablePartialStatus,
    clearStablePartialStatus,
    type StablePartialState,
} from "../services/stableAccumulatedRequest";

const BAR_Z = 10055;
// Sit just above the companion / regime badges so it doesn't cover them.
const BAR_BOTTOM = "calc(96px + env(safe-area-inset-bottom, 0px))";

const StableFetchProgressBanner: React.FC = () => {
    const [entries, setEntries] = useState<StablePartialState[]>([]);

    useEffect(() => {
        // Subscribe to live partial-status updates from stableAccumulatedRequest.
        const unsubscribe = subscribeStablePartialStatus(setEntries);
        return unsubscribe;
    }, []);

    // Aggregate the raw per-URL feed into user-facing buckets.
    const { loading, exhausted } = useMemo(() => {
        const loading = entries.filter(
            (e) => e.partial && !e.exhausted,
        );
        const exhausted = entries.filter((e) => e.exhausted);
        return { loading, exhausted };
    }, [entries]);

    if (loading.length === 0 && exhausted.length === 0) return null;

    const symbolsLoading = uniqSymbols(loading);
    const symbolsIncomplete = uniqSymbols(exhausted);

    return (
        <div
            data-mmec-stable-fetch-banner="open"
            role="status"
            aria-live="polite"
            style={{
                position: "fixed",
                left: 16,
                right: 16,
                bottom: BAR_BOTTOM,
                zIndex: BAR_Z,
                display: "flex",
                justifyContent: "center",
                pointerEvents: "none",
            }}
        >
            <div
                style={{
                    pointerEvents: "auto",
                    maxWidth: 640,
                    width: "100%",
                    borderRadius: 10,
                    border: `1px solid ${
                        exhausted.length > 0
                            ? "color-mix(in srgb, var(--warn) 55%, transparent)"
                            : "color-mix(in srgb, var(--acc) 45%, transparent)"
                    }`,
                    background:
                        exhausted.length > 0
                            ? "linear-gradient(var(--warn-dim), var(--warn-dim)), var(--elev)"
                            : "linear-gradient(var(--acc-dim), var(--acc-dim)), var(--elev)",
                    boxShadow: "var(--lift-3)",
                    color: "var(--txt)",
                    fontFamily: "var(--font-ui)",
                    fontSize: 12,
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                }}
            >
                {loading.length > 0 && (
                    <>
                        <Spinner />
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ color: "var(--acc)", fontWeight: 600 }}>
                                Loading remaining expirations…
                            </strong>{" "}
                            <span
                                style={{
                                    color: "var(--muted)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {describeSymbols(symbolsLoading)} ·{" "}
                                {Math.min(
                                    loading[0]?.attempt ?? 0,
                                    loading[0]?.maxAttempts ?? 12,
                                )}
                                /{loading[0]?.maxAttempts ?? 12}
                            </span>
                        </span>
                    </>
                )}

                {loading.length === 0 && exhausted.length > 0 && (
                    <>
                        <WarnIcon />
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ color: "var(--warn)", fontWeight: 600 }}>
                                Data may be incomplete
                            </strong>{" "}
                            <span
                                style={{
                                    color: "var(--muted)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {describeSymbols(symbolsIncomplete)} · stopped
                                after{" "}
                                {exhausted[0]?.maxAttempts ?? 12} attempts
                            </span>
                        </span>
                        <button
                            type="button"
                            title="Dismiss"
                            onClick={() =>
                                exhausted.forEach((e) =>
                                    clearStablePartialStatus(e.url),
                                )
                            }
                            style={dismissBtnStyle}
                        >
                            ✕
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

function uniqSymbols(list: StablePartialState[]): string[] {
    const out: string[] = [];
    for (const e of list) {
        if (e.symbol && !out.includes(e.symbol)) out.push(e.symbol);
    }
    return out;
}

function describeSymbols(symbols: string[]): string {
    if (symbols.length === 0) return "Stable data";
    if (symbols.length <= 3) return symbols.join(", ");
    return `${symbols.slice(0, 3).join(", ")} +${symbols.length - 3}`;
}

const dismissBtnStyle: React.CSSProperties = {
    background: "transparent",
    border: "1px solid var(--hair)",
    color: "var(--muted)",
    borderRadius: "var(--radius-sm)",
    height: 24,
    padding: "0 8px",
    fontSize: 11,
    fontFamily: "var(--font-ui)",
    cursor: "pointer",
    lineHeight: 1,
};

const Spinner: React.FC = () => (
    <span
        aria-hidden
        style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            border: "2px solid var(--acc-dim)",
            borderTopColor: "var(--acc)",
            animation: "mmecStableSpin 0.8s linear infinite",
            flex: "0 0 14px",
        }}
    />
);

const WarnIcon: React.FC = () => (
    <span aria-hidden style={{ fontSize: 14, color: "var(--warn)" }}>
        ⚠
    </span>
);

export default StableFetchProgressBanner;
